import publicUrls from "../../routes/publicUrls";
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography,
  Pagination, TextField, Button, InputAdornment
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Winning, getCurrentAUctionList, GetUpcomingAuctions } from "../Model/Auction";
import { encodeId } from "../../hash";
import { getUser } from "../../Model/LocalStorage";
import {
  C,
  FONT,
  accountRootSx,
  eyebrowSx,
  inputSx,
  paginationSx,
  primaryButtonSx,
  textButtonSx,
} from "./accountStyles";
import { useAccountEmbedded } from "./components/AccountContext";
import AccountLayout from "./components/AccountLayout";
import { SectionHeader } from "./components/AccountSection";
import BidSummary from "./components/BidSummary";
import LotCard, { LotGrid } from "./components/LotCard";
import { EmptyState, LoadingState } from "./components/primitives";




function bidStatus(lot) {
  const isUnsold = lot.lot_status === "UNSOLD";
  const isReserveNotMet = lot.lot_status === "RESERVE_NOT_MET";
  const isPass = lot.lot_status === "PASS";
  const isSold = lot.lot_status === "SOLD";
  const isEndedUnsuccessful = isUnsold || isReserveNotMet || isPass;

  let labelText = lot.isWinning ? "WINNING" : "OUTBID";
  if (isSold && lot.isWinning) labelText = "WIN";
  else if (isUnsold) labelText = "UNSOLD";
  else if (isReserveNotMet) labelText = "RESERVE NOT MET";
  else if (isPass) labelText = "PASSED";

  const isActuallyWinning = lot.isWinning && !isEndedUnsuccessful;
  return { labelText, isSold, isEndedUnsuccessful, isActuallyWinning };
}

function MyLots() {
  const [dataWinning, setDataWinning] = useState([]);
  const [currentWinning, setCurrentWinning] = useState([]);
  const [upcomingAuctions, setUpcomingAuctions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [userId, setUserId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  // ORIGINAL LOGIC: Initialize User
  useEffect(() => {
    const initializeUser = async () => {
      try {
        const getuser = await getUser();
        if (getuser) setUserId(getuser.id);
      } catch (error) {
        console.error("User initialization failed:", error);
      }
    };
    initializeUser();
  }, []);

  // ORIGINAL LOGIC: Fetch Upcoming
  const fetchUpcoming = useCallback(async () => {
    try {
      const results = await GetUpcomingAuctions();
      if (results && results.status) {
        const upcoming = results.data.filter(
          (x) => x.auction_status?.toUpperCase() === "UPCOMING" || x.auction_status?.toUpperCase() === "ONGOING"
        );
        setUpcomingAuctions(upcoming);
      }
    } catch (error) {
      console.error("Failed to fetch upcoming:", error);
    }
  }, []);

  // ORIGINAL LOGIC + FIX: Get Winning History
  const getWinning = useCallback(async (pageNum = 1, searchTxt = "") => {
    setLoading(true);
    try {
      const results = await Winning(pageNum, searchTxt);
      if (results && results.success && results.data) {
        setDataWinning(results.data);
        setTotal(Math.ceil((results.pagination?.total || 0) / 12));
      } else {
        setDataWinning([]);
        if (pageNum === 1) setTotal(0);
      }
    } catch (error) {
      setDataWinning([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ORIGINAL LOGIC: Current Bids
  const getCurrentUserLots = useCallback(async (aucId) => {
    if (!userId || !aucId) return [];
    try {
      const results = await getCurrentAUctionList(aucId);
      if (results && results.msg) {
        return results.msg
          .filter(item => item.data.userBid == userId || (item.myBid && item.myBid.length > 0))
          .map(item => ({
            ...item.data,
            images: item.images || [],
            myBid: item.myBid || [],
            highestUserBid: item.myBid?.length > 0 ? Math.max(...item.myBid.map(b => b.amount)) : 0,
            isWinning: item.data.userBid == userId,
            parent_auction_id: aucId
          }));
      }
      return [];
    } catch (error) { return []; }
  }, [userId]);

  useEffect(() => {
    const loadAllActiveBids = async () => {
      if (userId && upcomingAuctions.length > 0) {
        let allBids = [];
        for (let auc of upcomingAuctions) {
          const bids = await getCurrentUserLots(auc.id);
          allBids = [...allBids, ...bids];
        }
        setCurrentWinning(allBids);
      }
    };
    loadAllActiveBids();
  }, [userId, upcomingAuctions, getCurrentUserLots]);

  useEffect(() => {
    if (userId) getWinning(page, activeSearch);
  }, [userId, page, activeSearch, getWinning]);

  useEffect(() => {
    if (userId) fetchUpcoming();
  }, [userId, fetchUpcoming]);

  const handleSearchTrigger = () => { setPage(1); setActiveSearch(searchQuery); };
  const handleKeyPress = (e) => { if (e.key === "Enter") handleSearchTrigger(); };

  const handleChange = (event, value) => {
    setPage(value);
    const section = document.getElementById('winning-history-section');
    if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const gotoSinglePage = (auction_name, title, auction_id, lotNumber) => {
    const slug = auction_name ? auction_name.replace(/ /g, "-") : "auction";
    const cleanTitle = title ? title.replace(/[^a-zA-Z0-9-_]/g, "") : "untitled";

    window.location.href = publicUrls.lot(slug, encodeId(auction_id), lotNumber, `my-${cleanTitle}`);
  

  };

  const embedded = useAccountEmbedded();

  // Display-only grouping of the bids already loaded above (same auction filter as before).
  const auctionsWithBids = upcomingAuctions
    .map((auc) => ({ auc, lots: currentWinning.filter((lot) => lot.parent_auction_id === auc.id) }))
    .filter((group) => group.lots.length > 0);
  const shownBids = auctionsWithBids.reduce((all, group) => all.concat(group.lots), []);
  const winningCount = shownBids.filter((lot) => bidStatus(lot).isActuallyWinning).length;

  const content = (
    <Box sx={{ ...accountRootSx, width: "100%" }}>

      {auctionsWithBids.length > 0 && (
        <Box component="section" aria-labelledby="active-bids-title" sx={{ mb: { xs: 8, md: 11 } }}>
          <SectionHeader
            id="active-bids-title"
            title="Active Bids"
            description="Lots you are bidding on in current and upcoming auctions."
          />
            
          <BidSummary
            items={[
              { label: "Lots bid on", value: shownBids.length },
              { label: "Winning", value: winningCount, color: C.success },
              { label: "Outbid / Ended", value: shownBids.length - winningCount, color: C.danger },
            ]}
          />

                    {auctionsWithBids.map(({ auc, lots: lotsForThisAuction }) => (
                      <Box key={auc.id} sx={{ mb: { xs: 5, md: 7 } }}>
                      <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 2,
              mb: 1.5,
            }}
          >
            <Typography
              component="h3"
              sx={{
                fontFamily: FONT,
                fontSize: { xs: "1.2rem", md: "1.45rem" },
                color: C.ink,
                lineHeight: 1.3,
                minWidth: 0,
              }}
            >
              {auc.d_name}
            </Typography>

            <Typography
              sx={{
                ...eyebrowSx,
                fontWeight: 400,
                flexShrink: 0,
              }}
            >
              {lotsForThisAuction.length}{" "}
              {lotsForThisAuction.length === 1 ? "lot" : "lots"}
            </Typography>
          </Box>

          <Typography
            component="p"
            sx={{
              fontFamily: FONT,
              fontSize: { xs: "0.72rem", md: "0.78rem" },
              lineHeight: 1.5,
              color: "rgba(0,0,0,0.58)",
              maxWidth: "850px",
              mb: 3,
            }}
          >
            This is not the final result. The final result will be confirmed at
            the end of the auction, as phone bids, floor bids, and internet bids
            may still affect the outcome. In case of technical issues with the
            application, please verify the final result directly with Leon Gallery
            to confirm whether you are the winning bidder.
          </Typography>

              <LotGrid>
                {lotsForThisAuction.map((lot, index) => {
                  const { labelText, isSold, isEndedUnsuccessful, isActuallyWinning } = bidStatus(lot);
                  return (
                    <LotCard
                      key={index}
                      onClick={() => gotoSinglePage(auc.d_name, lot.title, auc.id, lot.lot_number)}
                      image={lot.images?.[0]?.img_url}
                      imageAlt={lot.title || ""}
                      ratio="1 / 1"
                      dimmed={isEndedUnsuccessful}
                      tag={{ label: labelText, tone: isActuallyWinning ? "success" : "danger" }}
                      eyebrow={`Lot ${lot.lot_number}`}
                      artist={lot.artist || "Unknown Artist"}
                      title={lot.title}
                      footerLabel={lot.highestUserBid > 0 ? "Your highest bid" : undefined}
                      footerValue={<>₱ {lot.highestUserBid?.toLocaleString()}</>}
                      footerTone={isActuallyWinning ? "success" : "danger"}
                      note={
                        isEndedUnsuccessful || isSold
                          ? { text: isSold ? "SALE COMPLETED" : "AUCTION ENDED", tone: isSold ? "success" : "danger" }
                          : null
                      }
                    />
                  );
                })}
              </LotGrid>
            </Box>
          ))}
        </Box>
      )}

      
      <Box component="section" aria-labelledby="winning-history-title" id="winning-history-section" sx={{ scrollMarginTop: "120px" }}>
        <SectionHeader
          id="winning-history-title"
          title="Winning History"
          description="Auction archives — lots you have won."
          actions={
            <Box role="search" sx={{ display: "flex", width: { xs: "100%", md: 380 } }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search auctions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                inputProps={{ "aria-label": "Search winning history" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: C.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...inputSx, "& .MuiOutlinedInput-root": { ...inputSx["& .MuiOutlinedInput-root"], height: 44 } }}
              />
              <Button variant="contained" onClick={handleSearchTrigger} disableElevation sx={{ ...primaryButtonSx, minHeight: 44, px: 3, flexShrink: 0 }}>
                Search
              </Button>
            </Box>
          }
        />

        <Box sx={{ minHeight: 400 }}>
          {loading ? (
            <LoadingState />
          ) : dataWinning.length > 0 ? (
            <LotGrid>
              {dataWinning.map((lot, index) => (
                <LotCard
                  key={index}
                  onClick={() => gotoSinglePage(lot.auction_name, lot.title, lot.auction_id, lot.lot_number)}
                  image={lot.img_url}
                  imageAlt={lot.title || ""}
                  ratio="4 / 5"
                  eyebrow={`Lot ${lot.lot_number}${lot.auction_name ? ` · ${lot.auction_name}` : ""}`}
                  artist={lot.artist || "Unknown Artist"}
                  title={lot.title}
                  footerValue={<>₱ {Number(lot.hammer_price).toLocaleString()}</>}
                />
              ))}
            </LotGrid>
          ) : (
            <EmptyState
              title="No Records Found"
              text="Try going back or clearing your search."
              action={
                page > 1 && (
                  <Button onClick={() => setPage(page - 1)} sx={textButtonSx}>
                    ← Back to Previous Page
                  </Button>
                )
              }
            />
          )}
        </Box>

        
        {(total > 1 || page > 1) && (
          <Box component="nav" aria-label="Winning history pages" sx={{ mt: 6, display: "flex", flexDirection: "column", alignItems: "center", borderTop: `1px solid ${C.line}`, pt: 4 }}>
            <Pagination
              count={total || page}
              page={page}
              onChange={handleChange}
              size="large"
              shape="rounded"
              showFirstButton
              showLastButton
              siblingCount={0}
              sx={paginationSx}
            />
            <Typography sx={{ ...eyebrowSx, mt: 2 }}>
              Page {page} {total > 0 ? `of ${total}` : ""}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  // Inside /my/account the header, tabs and container come from the account page.
  if (embedded) return content;
  return <AccountLayout title="My Lots">{content}</AccountLayout>;
}

export default MyLots;
